import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, AuthTokenRecord, ConnectorGuide } from '../../lib/api';

interface IssuedConnectorToken {
  token: string;
  masked: string;
  expiresAt?: string;
}

interface NjuSessionState {
  connected: boolean;
  expired: boolean;
  service?: string | null;
  expiresAt?: string | null;
  lastSyncAt?: string | null;
}

interface SyncOutcome {
  domain: string;
  dataType?: string;
  count?: number;
  accepted?: boolean;
  error?: string;
}

const ALL_SYNC_DOMAINS = ['roster', 'grades', 'schedule', 'plan', 'rank', 'youth', 'five'];

const DOMAIN_LABEL: Record<string, string> = {
  roster: '学籍', grades: '成绩', schedule: '课表', plan: '培养方案',
  rank: '排名·四六级', youth: '志愿时长', five: '五育',
};

function tillLabel(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('zh-CN');
}

function dateLabel(value?: string) {
  if (!value) return '尚未使用';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '时间未知';
  const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
  return days === 0 ? '今天使用过' : `${days} 天前使用`;
}

export function DataConnectionPanel() {
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState<ConnectorGuide>();
  const [tokens, setTokens] = useState<AuthTokenRecord[]>([]);
  const [issued, setIssued] = useState<IssuedConnectorToken>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [downloadAvailable, setDownloadAvailable] = useState<boolean>();

  /* ---------- 服务端南大会话（方案 A）---------- */
  const [nju, setNju] = useState<NjuSessionState>();
  const [njuOpen, setNjuOpen] = useState(false);
  const [njuUser, setNjuUser] = useState('');
  const [njuPass, setNjuPass] = useState('');
  const [njuBusy, setNjuBusy] = useState(false);
  const [njuMsg, setNjuMsg] = useState('');
  const [syncNote, setSyncNote] = useState('');

  const loadNju = useCallback(async () => {
    try {
      const result = await api<{ data: NjuSessionState }>('/api/v1/sync/session');
      setNju(result.data);
    } catch { /* 未登录或后端不支持时静默 */ }
  }, []);

  useEffect(() => { void loadNju(); }, [loadNju]);

  const connectNju = async () => {
    if (!njuUser || !njuPass) { setNjuMsg('请填写学号与密码'); return; }
    setNjuBusy(true); setNjuMsg('正在完成南大统一身份认证…');
    try {
      const result = await api<{ data: { expiresAt?: string; synced?: SyncOutcome[] } }>('/api/v1/sync/session', {
        method: 'POST', body: JSON.stringify({ username: njuUser, password: njuPass }),
      });
      setNjuPass('');
      const synced = result.data?.synced || [];
      const ok = synced.filter((item) => !item.error);
      setSyncNote(ok.length ? `已同步 ${ok.length} 个数据域：${ok.map((item) => `${item.domain} ${item.count ?? 0} 条`).join('、')}` : '');
      setNjuMsg('连接成功。密码未被保存，仅加密保存会话用于取数。');
      await loadNju();
    } catch (error) {
      setNjuMsg(error instanceof Error ? error.message : '连接失败');
    } finally { setNjuBusy(false); }
  };

  const runNjuSync = async () => {
    setNjuBusy(true); setNjuMsg('正在同步（成绩/课表较慢，请稍候）…'); setSyncNote('');
    try {
      const result = await api<{ data: { synced: SyncOutcome[] } }>('/api/v1/sync/run', {
        method: 'POST', body: JSON.stringify({ domains: ALL_SYNC_DOMAINS }),
      });
      const synced = result.data?.synced || [];
      const ok = synced.filter((item) => !item.error);
      const failed = synced.filter((item) => item.error);
      setSyncNote([
        ok.length ? `已更新：${ok.map((item) => `${DOMAIN_LABEL[item.domain] || item.domain} ${item.count ?? 0} 条`).join('、')}` : '',
        failed.length ? `未更新：${failed.map((item) => `${DOMAIN_LABEL[item.domain] || item.domain}（${String(item.error).trim()}）`).join('；')}` : '',
      ].filter(Boolean).join('　|　'));
      setNjuMsg('同步完成。');
      await loadNju();
    } catch (error) {
      setNjuMsg(error instanceof Error ? error.message : '同步失败');
    } finally { setNjuBusy(false); }
  };

  const disconnectNju = async () => {
    setNjuBusy(true); setNjuMsg('');
    try {
      await api('/api/v1/sync/session', { method: 'DELETE' });
      setNju(undefined); setSyncNote(''); setNjuMsg('已断开，服务端已删除该会话。');
      await loadNju();
    } catch (error) { setNjuMsg(error instanceof Error ? error.message : '断开失败'); }
    finally { setNjuBusy(false); }
  };

  const syncAssignments = async () => {
    setNjuBusy(true); setNjuMsg('正在读取教学平台作业…');
    try {
      const result = await api<{ data: { fetched: number; delivered: number; skipped: number } }>('/api/v1/inbox/assignments', { method: 'POST', body: '{}' });
      setNjuMsg(`作业同步完成：平台返回 ${result.data.fetched} 项，${result.data.delivered} 项进入收件箱，跳过 ${result.data.skipped} 项重复。`);
    } catch (error) {
      const raw = error instanceof Error ? error.message : '作业同步失败';
      setNjuMsg(/NO_SESSION|未连接|已过期|重新登录/.test(raw) ? '南大账号未连接或会话已过期，请点「管理 → 重新连接」。' : raw);
    } finally { setNjuBusy(false); }
  };

  const connectorTokens = useMemo(() => tokens.filter((token) => token.label === '本机同步器' && !token.expired && !token.revokedAt), [tokens]);
  const latestConnector = connectorTokens[0];

  const load = useCallback(async () => {
    const [guideResult, tokenResult] = await Promise.allSettled([
      api<{ data: ConnectorGuide }>('/api/v1/connector/guide'),
      api<{ tokens: AuthTokenRecord[] }>('/auth/tokens'),
    ]);
    if (guideResult.status === 'fulfilled') setGuide(guideResult.value.data);
    if (tokenResult.status === 'fulfilled') setTokens(tokenResult.value.tokens || []);
    if (guideResult.status === 'rejected') setMessage(guideResult.reason instanceof Error ? guideResult.reason.message : '连接指南加载失败');
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); setIssued(undefined); } };
    window.addEventListener('keydown', close);
    void fetch('/downloads/caigen-connector.exe', { method: 'HEAD' }).then((response) => setDownloadAvailable(response.ok)).catch(() => setDownloadAvailable(false));
    return () => window.removeEventListener('keydown', close);
  }, [open]);

  const issue = async () => {
    setBusy(true); setMessage('');
    try {
      const result = await api<IssuedConnectorToken>('/auth/tokens', { method: 'POST', body: JSON.stringify({ purpose: 'connector', scope: 'full' }) });
      setIssued(result);
      setMessage('绑定令牌已生成。完整内容只在本次窗口中显示。');
      await load();
    } catch (error) { setMessage(error instanceof Error ? error.message : '绑定令牌生成失败'); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!connectorTokens.length || busy) return;
    setBusy(true); setMessage('');
    try {
      const results = await Promise.allSettled(connectorTokens.map((token) => api(`/auth/tokens/${encodeURIComponent(token.id)}`, { method: 'DELETE' })));
      const failures = results.filter((result) => result.status === 'rejected').length;
      setIssued(undefined);
      await load();
      if (failures) setMessage(`${connectorTokens.length - failures} 个绑定已解除，${failures} 个解除失败，请重试。`);
      else setMessage(connectorTokens.length > 1 ? `已解除 ${connectorTokens.length} 个本机同步器绑定。` : '本机同步器绑定已解除。');
    } catch (error) { setMessage(error instanceof Error ? error.message : '解除绑定失败'); }
    finally { setBusy(false); }
  };

  const copy = async (value: string, success: string) => {
    try { await navigator.clipboard.writeText(value); setMessage(success); }
    catch { setMessage('浏览器未允许复制，请手动选择文本。'); }
  };

  const closeModal = () => { setOpen(false); setIssued(undefined); };
  const connectorState = latestConnector ? (latestConnector.lastUsedAt ? dateLabel(latestConnector.lastUsedAt) : '已绑定，等待首次同步') : '未绑定';

  return <>
    <section className="paper-card data-connection-panel" id="data-connection">
      <header><div><small>NJU · DATA CONNECTION</small><h3>南大数据连接</h3><p>公开信息自动更新；个人数据可手动填写，也可连接南大账号后由服务端取回。</p></div><span>会话加密保存 · 可随时断开</span></header>
      <div className="connection-paths">
        <article className="connected"><i>公</i><div><small>PUBLIC SIGNAL</small><h4>校园公开信息</h4><p>通知、政策、培养方案与规则库，无需登录南大。</p></div><em>已连接</em></article>
        <article className="available"><i>填</i><div><small>MANUAL DATA · 推荐</small><h4>手动补全画像</h4><p>零安装，成绩、排名、志愿、体测与五育都可填写。</p></div><a href="#manual-data">现在填写</a></article>
        <article className={nju?.connected ? 'connected' : nju?.expired ? 'pending' : 'available'}><i>南</i><div><small>ACCOUNT SYNC · 服务端</small><h4>连接南大账号</h4><p>{nju?.connected
          ? `已连接${tillLabel(nju.expiresAt) ? `，会话有效至 ${tillLabel(nju.expiresAt)}` : ''}${nju.lastSyncAt ? `；上次同步 ${dateLabel(nju.lastSyncAt)}` : ''}。`
          : nju?.expired ? '会话已过期，重新连接即可继续自动同步。' : '用一次学号密码换取会话，服务端加密保存会话（不含密码），自动取回成绩、课表、培养方案与教学平台作业。'}</p></div><button type="button" onClick={() => setNjuOpen(true)}>{nju?.connected ? '管理' : nju?.expired ? '重新连接' : '连接南大账号'}</button></article>
        <article className={latestConnector ? 'connected connector' : 'pending connector'}><i>校</i><div><small>LOCAL CONNECTOR · 进阶</small><h4>eHall 一键同步</h4><p>{latestConnector ? connectorState : '在自己的电脑运行连接器，登录南大后选择数据域。'}</p></div><button type="button" onClick={() => setOpen(true)}>{latestConnector ? '管理连接' : '连接本机'}</button></article>
      </div>
      <p className="connection-correction">登录密码只用于当次换取会话、用完即弃；服务端仅加密保存会话凭据（约 14 天，可随时断开），不保存密码。</p>
    </section>

    {open ? <div className="connection-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModal(); }}>
      <section className="paper-card connection-modal connector-guide-modal" role="dialog" aria-modal="true" aria-labelledby="connection-modal-title">
        <header><div><small>LOCAL-FIRST · CAS</small><h2 id="connection-modal-title">{guide?.title || '连接本机'}</h2><p>{guide?.summary || '正在读取连接器指南…'}</p></div><button type="button" aria-label="关闭" onClick={closeModal}>×</button></header>

        <div className="connector-state-line"><span className={latestConnector ? 'bound' : ''}><i />{connectorState}</span>{latestConnector?.expiresAt ? <small>有效至 {new Date(latestConnector.expiresAt).toLocaleDateString('zh-CN')}</small> : <small>令牌有效期 7 天</small>}</div>

        <section className="connector-primary-path" aria-label="推荐方式">
          <div><small>RECOMMENDED · ZERO INSTALL</small><h3>不安装，也能获得完整画像</h3><p>{guide?.alternatives?.[0]?.detail || '直接在页面手动补全个人数据。'}</p></div><a className="primary-button" href="#manual-data" onClick={closeModal}>去手动填写</a>
        </section>

        <details className="connector-advanced" open>
          <summary><span>进阶方式 · 一键同步</span><small>Windows 本机程序</small></summary>
          {guide ? <div className="connector-guide-body">
            <div className="connector-requirements">{guide.requirements.map((item) => <span key={item.label}><small>{item.label}</small><b>{item.value}</b></span>)}</div>
            <ol>{guide.steps.map((step) => <li key={step.n}><i>{step.n}</i><div><b>{step.title}</b><p>{step.detail}</p>{step.code ? <code>{step.code}</code> : null}</div></li>)}</ol>

            <section className="connector-token-box">
              <div><small>ACCOUNT BINDING</small><h3>{latestConnector ? '本机已建立绑定' : '生成专用绑定令牌'}</h3><p>令牌只用于向当前菜根账户上传你选择的数据，不是南大密码。</p></div>
              {issued ? <output><span>{issued.token}</span><small>{issued.masked} · 关闭窗口后不再显示全量</small></output> : null}
              <div className="connector-token-actions">
                {issued ? <button className="primary-button" type="button" onClick={() => void copy(issued.token, '完整令牌已复制。')}>复制绑定令牌</button> : <button className="primary-button" type="button" disabled={busy} onClick={() => void issue()}>{latestConnector ? '重新生成令牌' : '生成绑定令牌'}</button>}
                {latestConnector ? <button className="quiet-button danger" type="button" disabled={busy} onClick={() => void revoke()}>解除绑定</button> : null}
              </div>
            </section>

            <div className="connector-install-actions">
              {downloadAvailable ? <a className="quiet-button" href="/downloads/caigen-connector.exe" download>下载 Windows 连接器</a> : <button className="quiet-button" type="button" disabled>安装包待发布</button>}
              {guide.advanced?.code ? <button className="quiet-button" type="button" onClick={() => void copy(guide.advanced!.code, '同步命令已复制。')}>复制高级命令</button> : null}
            </div>

            <div className="connector-scopes"><h3>可选择的数据域</h3>{guide.scopes.map((scope) => <span key={scope.key}><b>{scope.label}</b><small>{scope.detail}</small></span>)}</div>
            <details className="connector-faq"><summary>常见问题与安全说明</summary><div>{guide.troubleshooting.map((item) => <p key={item.q}><b>{item.q}</b><span>{item.a}</span></p>)}</div><ul>{guide.privacy.map((item) => <li key={item}>{item}</li>)}</ul></details>
          </div> : <p className="connector-loading">{message || '正在读取服务端指南…'}</p>}
        </details>
        {message ? <p className="connector-message" role="status">{message}</p> : null}
      </section>
    </div> : null}
    {njuOpen ? <div className="connection-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) { setNjuOpen(false); setNjuMsg(''); } }}>
      <section className="paper-card connection-modal" role="dialog" aria-modal="true" aria-labelledby="nju-modal-title">
        <header><div><small>NJU · ACCOUNT SYNC</small><h2 id="nju-modal-title">连接南大账号</h2><p>登录密码只用于当次换取会话，用完即弃；服务端加密保存会话凭据用于取数。</p></div><button type="button" aria-label="关闭" onClick={() => { setNjuOpen(false); setNjuMsg(''); }}>×</button></header>

        {nju?.connected ? <div className="connector-token-box">
          <div><small>SESSION</small><h3>已连接南大</h3><p>{tillLabel(nju.expiresAt) ? `会话有效至 ${tillLabel(nju.expiresAt)}` : '会话有效'}{nju.lastSyncAt ? ` · 上次同步 ${dateLabel(nju.lastSyncAt)}` : ''}</p></div>
          <div className="connector-token-actions">
            <button className="primary-button" type="button" disabled={njuBusy} onClick={() => void runNjuSync()}>立即同步画像数据</button>
            <button className="quiet-button" type="button" disabled={njuBusy} onClick={() => void syncAssignments()}>同步教学平台作业</button>
            <button className="quiet-button danger" type="button" disabled={njuBusy} onClick={() => void disconnectNju()}>断开连接</button>
          </div>
        </div> : <form className="connector-token-box" onSubmit={(event) => { event.preventDefault(); void connectNju(); }}>
          <div><small>CREDENTIALS · 仅当次使用</small><h3>{nju?.expired ? '会话已过期，请重新连接' : '用学号密码完成一次授权'}</h3><p>不会保存密码；换到的会话约 14 天有效，到期或断开后需重新授权。</p></div>
          <label className="connection-field"><span>学号 / 统一身份认证账号</span><input value={njuUser} autoComplete="username" onChange={(event) => setNjuUser(event.target.value)} placeholder="如 261220051" /></label>
          <label className="connection-field"><span>密码</span><input type="password" value={njuPass} autoComplete="current-password" onChange={(event) => setNjuPass(event.target.value)} placeholder="只用于本次登录" /></label>
          <div className="connector-token-actions">
            <button className="primary-button" type="submit" disabled={njuBusy}>{njuBusy ? '正在连接…' : '连接并同步'}</button>
            <button className="quiet-button" type="button" disabled={njuBusy} onClick={() => { setNjuUser(''); setNjuPass(''); setNjuMsg(''); }}>清空</button>
          </div>
        </form>}

        {njuMsg ? <p className="connector-message" role="status">{njuMsg}</p> : null}
        {syncNote ? <p className="connector-message" role="status">{syncNote}</p> : null}
      </section>
    </div> : null}
  </>;
}
